import "mapbox-gl/dist/mapbox-gl.css";
import React, { useState, useRef, useEffect } from "react";
import MapGL, { Marker, Source, Layer, MapRef, ViewState, MapMouseEvent, LngLat } from "react-map-gl";

import RenderHazardMap from "../riskAssessment/RenderHazardMap";
import { defaultSourceLayer } from "../riskAssessment/defaultSourceLayer";
import { defaultRiskAssessmentMapStyle } from "../map/Constants";
import { useHazardMap } from "../riskAssessment/HazardMapContext";
import Pin from "../map/pin";
import BuildingHeightLegend from "../legends/BuildingHeightLegend";
import { detectNeedForExplosionPopup, renderExplosionPopup } from "./explosionPopupUtils";
import { ScaleBar } from "../map/ScaleBar";
import { MapStylesMenu } from "../map/MapStylesMenu";

type HazardAssessmentMapProps = {
  latitude: number;
  longitude: number;
  buildingGeoJsonProp: GeoJsonData;
};

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export const PolicyShowMap: React.FC<HazardAssessmentMapProps> = ({
  latitude,
  longitude,
  buildingGeoJsonProp,
}) => {
  const { selectedHazard, onHazardChange } = useHazardMap();

  const [viewState, setViewState] = useState({
    latitude: latitude,
    longitude: longitude,
    zoom: 17,
  });

  const [mapStyle, setMapStyle] = useState(defaultRiskAssessmentMapStyle); // when hazard config has mayStyle
  const [sourceLayerData, setSourceLayerData] = useState(defaultSourceLayer); // when hazard config has source layer
  const [sourceLayerDataSecondary, setSourceLayerDataSecondary] = useState(defaultSourceLayer); // when hazard config has 2nd source layer
  const [explosionInfo, setExplosionInfo] = useState<any>(null);
  const mapRef = useRef<MapRef>(null);
  const [isBasicMapStyle, setIsBasicMapStyle] = useState(true);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const styleRevRef = useRef(0);

  const onStyleChange = (newStyle: string) => {
    if (mapStyle !== newStyle) {
      console.log("🔁 Changing map style to", newStyle);
      styleRevRef.current += 1; // force Map remount
      setStyleLoaded(false); // gate Sources until onLoad
      setMapStyle(newStyle);
    } else {
      // console.log("ℹ️ Map style already set to", newStyle);
    }
  };

  const onMove = (viewState: ViewState) => {
    setViewState(viewState);
  };

  const handleOnHover = (event: MapMouseEvent) => {
    const map = mapRef.current?.getMap();
    if (map && event.point) {
      // Design note: make sure you check event.point and isStyleLoaded() !
      // Otherwise if you move mouse during "refresh" of the map, you will  get an error:
      // Cannot read properties of undefined (reading 'featuresets')
      // at To.getFeaturesetDescriptors
      // at To.queryRenderedFeatureset
      // at Map.queryRenderedFeatures

      if (map.isStyleLoaded() === false) {
        // console.log("map is not loaded yet");
        return;
      }
      const features: any[] = map.queryRenderedFeatures(event.point);
      const coordinates: LngLat = event.lngLat;
      detectNeedForExplosionPopup(features, coordinates, setExplosionInfo);
    }
  };

  const setMapStyleAndClearHazardLayer = (mapStyle: string) => {
    console.log("🔁 Clearing hazard layer and changing map style to", mapStyle);
    onHazardChange(null);
    // setMapStyle(mapStyle);
    onStyleChange(mapStyle);
    setSourceLayerData(defaultSourceLayer);
    setSourceLayerDataSecondary(defaultSourceLayer);
    setIsBasicMapStyle(true);
  };

  return (
    <MapGL
      key={`map-${styleRevRef.current}`} // force full remount on style change
      {...viewState}
      mapboxAccessToken={TOKEN}
      style={{ width: "100%", height: "100%" }}
      mapStyle={mapStyle}
      onLoad={() => {
        setStyleLoaded(true);
        // console.log("✅ Map loaded; style ready. rev:", styleRevRef.current, "style:", mapStyle);
      }}
      onMove={(evt) => onMove(evt.viewState)}
      ref={mapRef}
      onMouseMove={(evt) => handleOnHover(evt)}
    >
      <RenderHazardMap
        setMapStyle={onStyleChange}
        selectedHazard={selectedHazard}
        sourceLayerData={sourceLayerData}
        setSourceLayerData={setSourceLayerData}
        sourceLayerDataSecondary={sourceLayerDataSecondary}
        setSourceLayerDataSecondary={setSourceLayerDataSecondary}
        styleLoaded={styleLoaded}
        isBasicMapStyle={isBasicMapStyle}
        setBasicMapStyle={setIsBasicMapStyle}
        location={{ lng: longitude, lat: latitude } as LngLat}
      />

      {/* {!styleLoaded && console.log("⏳ Skipping <Source> polygons because styleLoaded is false")} */}
      {styleLoaded &&
        buildingGeoJsonProp &&
        (() => {
          return (
            <Source
              key={`polygons-${styleRevRef.current}`}
              id="polygons"
              type="geojson"
              data={buildingGeoJsonProp}
            >
              <Layer
                id="polygons-fill"
                type="fill"
                source="polygons"
                paint={{ "fill-color": "#2296F3", "fill-opacity": 0.5 }}
              />
            </Source>
          );
        })()}

      <BuildingHeightLegend mapStyle={mapStyle} />

      <Marker longitude={longitude} latitude={latitude}>
        <Pin size={20} />
      </Marker>

      {renderExplosionPopup(explosionInfo)}

      <MapStylesMenu setMapStyle={setMapStyleAndClearHazardLayer}></MapStylesMenu>

      <ScaleBar zoom={viewState.zoom} />
    </MapGL>
  );
};
